import { Component, Inject, OnInit, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterState, TitleStrategy, RouterOutlet } from '@angular/router';
import { Settings } from 'luxon';
import { filter, switchMap, take } from 'rxjs/operators';
import { supportedBrowsers } from '../supported-browsers';
import { LoginService } from './core/authentication/login/login.service';
import { UserRole } from './core/authentication/login/user.model';
import { ConfigurationService } from './core/configuration/configuration.service';
import { CustomDateFormat } from './core/layout/custom-date-picker/custom-date-format';
import { dateFormatLocales } from './core/pipes/datetime.pipe';
import { SHORT_LOCALE_ID, TemplatePageTitleStrategy } from './core/shared/initialize.provider';
import { ThemeService } from './core/theme/theme.service';
import Utils from './core/utils/utils';
import './core/extensions/form.extension';
import { NotificationService } from './core/error/notification.service';
import { UserPreferenceService } from './core/utils/user-preference.service';
import { SessionKeepAliveService } from './core/authentication/login/session-keep-alive.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  constructor(
    @Inject(SHORT_LOCALE_ID) public locale: string,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private themeService: ThemeService,
    private loginService: LoginService,
    @Inject(MAT_DATE_FORMATS) public matDateFormat: CustomDateFormat,
    private configurationService: ConfigurationService,
    private router: Router,
    @Inject(TitleStrategy) private titleTemplate: TemplatePageTitleStrategy,
    private notificationService: NotificationService,
    private cacheService: UserPreferenceService,
    private sessionKeepAliveService: SessionKeepAliveService
  ) {
    Utils.setColors();
    Settings.defaultLocale = Utils.getLocaleFormat(this.locale);

    this.themeService.updateTheme();
    this.loginService.ssoConfig(this.route.snapshot.queryParamMap.get('returnUrl')).subscribe();

    if (!supportedBrowsers.test(navigator.userAgent)) {
      const snackBarRef = this.snackBar.open(
        $localize`Your browser is not supported.`,
        $localize`Update it!`,
        {
          duration: 10000,
        }
      );
      snackBarRef.onAction().subscribe(() => {
        window.open('https://browser-update.org/update-browser.html', '_blank');
      });
      return;
    }
  }

  ngOnInit(): void {
    this.sessionKeepAliveService.start();

    // The theme and the date format were read before the user cache was pulled: read them again.
    this.cacheService.hydrated$.subscribe(() => {
      const pulled = Utils.getLocaleFormat(this.locale);
      // The cache comes from the server now, which may hold a locale this version cannot format:
      // same fallback as `Utils.setDateLocaleFormat`, rather than a throw taking the theme below.
      const locale = dateFormatLocales.has(pulled) ? pulled : 'en-GB';
      Settings.defaultLocale = locale;
      // Read once at bootstrap, so the pickers would keep parsing on the locale of the first frame.
      this.matDateFormat.locale = locale;
      this.matDateFormat.updateDateFormat({ dateInput: dateFormatLocales.get(locale).DATETIME });
      this.themeService.updateTheme();
    });

    this.loginService.token$
      .pipe(
        filter(token => !!token),
        take(1),
        switchMap(() => this.loginService.hasRoles([UserRole.ROLE_SUPERUSER])),
        filter(isAdmin => !!isAdmin),
        switchMap(() => this.configurationService.getMetaData()),
        filter(meta => !!meta)
      )
      .subscribe({
        next: (meta: any) => {
          if (meta) {
            this.titleTemplate.setCustomer(meta.customer as string);
            const state: RouterState = this.router.routerState;
            this.titleTemplate.updateTitle(state.snapshot);
          }
        },
        error: () => {
          this.notificationService.showError(
            $localize`There is an issue with token. Please contact your administrator.`
          );
        },
      });
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.cacheService.pushOnUnload();
  }
}
